/*******************************************************************************
 * Copyright (c) 2024-2025 Passport Fintech Inc. All Rights Reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 ******************************************************************************/

import {NgZone} from '@angular/core';

/**
 * Hides the Jira Service Desk widget's default help button at all times,
 * including after it is re-rendered when the user opens/closes the widget
 * (opening it programmatically re-renders the button and wipes the inline
 * style we set, so it must be watched continuously, not just hidden once).
 * Watches the DOM with MutationObserver instead of polling on an interval,
 * and runs outside NgZone so it never triggers Angular change detection.
 * @param ngZone - used to keep the DOM watching logic outside Angular's zone
 */
export function hideJsdWidgetHelpButton(ngZone: NgZone): void {
  ngZone.runOutsideAngular(() => {
    let observedButton: HTMLElement | null = null;

    const forceHideButton = (button: HTMLElement): void => {
      if (button.style.display !== 'none') button.style.display = 'none';
    };

    // The widget's own click handling can re-render the button (e.g. when it
    // is opened/closed), which resets the inline style we set. Watching the
    // button node itself keeps re-hiding it whenever that happens.
    const watchButton = (button: HTMLElement): void => {
      if (observedButton === button) return;
      observedButton = button;
      forceHideButton(button);
      const buttonObserver = new MutationObserver(() => forceHideButton(button));
      buttonObserver.observe(button, {attributes: true, attributeFilter: ['style', 'class']});
    };

    const hideButtonAndBindClose = (iframeContent: Document): boolean => {
      const button = iframeContent.querySelector('#help-button') as HTMLElement | null;
      if (!button) return false;

      watchButton(button);
      const closeButton = iframeContent.querySelector('.close-icon');
      closeButton?.children[0]?.setAttribute('pointer-events', 'none');
      return true;
    };

    const watchIframeContent = (iframe: HTMLIFrameElement): void => {
      let contentObserver: MutationObserver | null = null;

      const tryHideButton = (): boolean => {
        const iframeContent = iframe.contentDocument;
        return !!iframeContent && hideButtonAndBindClose(iframeContent);
      };

      const observeContent = (): void => {
        const iframeContent = iframe.contentDocument;
        if (!iframeContent?.body) return;

        // Keep observing indefinitely (do not disconnect after the first
        // match): if the widget replaces the button node entirely - rather
        // than just changing its style - a fresh node needs to be re-hidden
        // and re-watched too.
        contentObserver?.disconnect();
        contentObserver = new MutationObserver(() => tryHideButton());
        contentObserver.observe(iframeContent.body, {childList: true, subtree: true});
      };

      tryHideButton();
      observeContent();

      // The iframe may still be showing its initial empty document; once it
      // finishes navigating to the real widget content, rebind the observer.
      iframe.addEventListener('load', () => {
        observedButton = null;
        tryHideButton();
        observeContent();
      });
    };

    const existingIframe = document.querySelector('#jsd-widget') as HTMLIFrameElement | null;
    if (existingIframe) {
      watchIframeContent(existingIframe);
      return;
    }

    const bodyObserver = new MutationObserver(() => {
      const iframe = document.querySelector('#jsd-widget') as HTMLIFrameElement | null;
      if (iframe) {
        bodyObserver.disconnect();
        watchIframeContent(iframe);
      }
    });
    bodyObserver.observe(document.body, {childList: true, subtree: true});
  });
}
